import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Trash2,
  CheckCircle2,
  Eye,
  Loader2,
} from 'lucide-react';
import { Pill } from '@/components/ui/pill';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { fmtBRL, fmtCpfCnpj, fmtDate } from '@/lib/utils';
import {
  useDeletePagamento,
  useUpdatePagamentoStatus,
  type Pagamento,
  type PagamentoStatus,
} from '@/lib/hooks/pagamentos';

interface Props {
  workspaceId: string;
  data: Pagamento[];
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
  onPageChange: (offset: number) => void;
}

const STATUS_TONE: Record<PagamentoStatus, 'warn' | 'pass' | 'block'> = {
  PENDENTE: 'warn',
  CONCILIADO: 'pass',
  CONTESTADO: 'block',
};

const STATUS_LABEL: Record<PagamentoStatus, string> = {
  PENDENTE: 'pendente',
  CONCILIADO: 'conciliado',
  CONTESTADO: 'contestado',
};

export function PagamentosTable({
  workspaceId,
  data,
  total,
  limit,
  offset,
  loading,
  onPageChange,
}: Props) {
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Pagamento | null>(null);
  const deleteMut = useDeletePagamento(workspaceId);
  const statusMut = useUpdatePagamentoStatus(workspaceId);

  function askDelete(p: Pagamento) {
    setOpenRow(null);
    setConfirmDelete(p);
  }

  async function confirmDeleteNow() {
    if (!confirmDelete) return;
    try {
      await deleteMut.mutateAsync(confirmDelete.id);
      toast.success('Pagamento removido.');
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao remover.');
    }
  }

  async function handleConciliar(id: string) {
    setOpenRow(null);
    try {
      await statusMut.mutateAsync({ id, status: 'CONCILIADO' });
      toast.success('Marcado como conciliado.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao atualizar.');
    }
  }

  const columns = useMemo<ColumnDef<Pagamento>[]>(
    () => [
      {
        accessorKey: 'dataPagamento',
        header: 'Data',
        cell: ({ row }) => (
          <span className="text-sm text-fg whitespace-nowrap">
            {fmtDate(row.original.dataPagamento)}
          </span>
        ),
        size: 100,
      },
      {
        accessorKey: 'credorNome',
        header: 'Credor',
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="text-sm font-medium text-fg truncate">{row.original.credorNome}</p>
            <p className="text-[11px] text-fg-muted font-mono">
              {fmtCpfCnpj(row.original.credorCpfCnpj)}
            </p>
          </div>
        ),
      },
      {
        accessorKey: 'categoriaDespesa',
        header: 'Categoria',
        cell: ({ row }) => (
          <span className="text-sm text-fg-muted truncate block max-w-[180px]">
            {row.original.categoriaDespesa ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'formaPagamento',
        header: 'Forma',
        cell: ({ row }) => (
          <span className="text-xs text-fg-muted font-mono">
            {row.original.formaPagamento ?? '—'}
          </span>
        ),
        size: 90,
      },
      {
        accessorKey: 'valor',
        header: () => <span className="block text-right">Valor</span>,
        cell: ({ row }) => (
          <span className="block text-right font-mono text-sm font-semibold text-fg tabular-nums">
            {fmtBRL(row.original.valor)}
          </span>
        ),
        size: 130,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Pill tone={STATUS_TONE[row.original.status]} dot>
            {STATUS_LABEL[row.original.status]}
          </Pill>
        ),
        size: 130,
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Ações</span>,
        cell: ({ row }) => {
          const p = row.original;
          const isOpen = openRow === p.id;
          return (
            <div className="relative flex justify-end">
              <button
                type="button"
                onClick={() => setOpenRow(isOpen ? null : p.id)}
                className="grid h-8 w-8 place-items-center rounded-1 text-fg-muted hover:text-fg hover:bg-card-soft"
                aria-label="Ações"
              >
                <MoreHorizontal className="h-4 w-4" strokeWidth={1.8} />
              </button>
              {isOpen && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setOpenRow(null)}
                    aria-hidden
                  />
                  <div className="absolute right-0 top-9 z-40 w-44 rounded-2 border border-card-stroke bg-card shadow-clay p-1">
                    {p.comprovanteR2Key && (
                      <button
                        type="button"
                        onClick={() => setOpenRow(null)}
                        className="flex w-full items-center gap-2 rounded-1 px-2 py-1.5 text-sm text-fg hover:bg-card-soft"
                      >
                        <Eye className="h-4 w-4" strokeWidth={1.8} />
                        Ver comprovante
                      </button>
                    )}
                    {p.status !== 'CONCILIADO' && (
                      <button
                        type="button"
                        onClick={() => handleConciliar(p.id)}
                        className="flex w-full items-center gap-2 rounded-1 px-2 py-1.5 text-sm text-fg hover:bg-mint-soft hover:text-mint-deep"
                      >
                        <CheckCircle2 className="h-4 w-4" strokeWidth={1.8} />
                        Conciliar
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => askDelete(p)}
                      className="flex w-full items-center gap-2 rounded-1 px-2 py-1.5 text-sm text-warn hover:bg-warn-soft"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                      Excluir
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        },
        size: 60,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openRow, deleteMut.isPending, statusMut.isPending],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(offset / limit) + 1;
  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  return (
    <div className="rounded-3 border border-card-stroke bg-card shadow-1 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-card-stroke bg-card-soft/60">
                {hg.headers.map((h) => (
                  <th
                    key={h.id}
                    style={{ width: h.column.columnDef.size }}
                    className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted"
                  >
                    {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-fg-muted">
                  <Loader2 className="inline h-4 w-4 animate-spin mr-2" strokeWidth={1.8} />
                  Carregando…
                </td>
              </tr>
            )}
            {!loading && table.getRowModel().rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-fg-muted text-sm">
                  Nenhum pagamento encontrado.
                </td>
              </tr>
            )}
            {!loading &&
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-card-stroke/50 last:border-b-0 hover:bg-card-soft/40 transition-colors"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3 align-middle">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        tone="danger"
        title="Excluir este pagamento?"
        description={
          confirmDelete
            ? `${confirmDelete.credorNome} · ${fmtBRL(confirmDelete.valor)}. Esta ação não pode ser desfeita.`
            : undefined
        }
        confirmLabel="Excluir"
        loading={deleteMut.isPending}
        onConfirm={confirmDeleteNow}
      />

      {total > 0 && (
        <div className="flex items-center justify-between gap-2 border-t border-card-stroke px-4 py-3 bg-card-soft/40">
          <p className="text-xs text-fg-muted">
            Página <span className="text-fg font-medium">{currentPage}</span> de{' '}
            <span className="text-fg font-medium">{totalPages}</span> · {total}{' '}
            {total === 1 ? 'lançamento' : 'lançamentos'}
          </p>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!canPrev}
              onClick={() => onPageChange(Math.max(0, offset - limit))}
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.8} />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!canNext}
              onClick={() => onPageChange(offset + limit)}
            >
              <ChevronRight className="h-4 w-4" strokeWidth={1.8} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
